/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { memo, FC } from 'react';

import classNames from 'classnames';

import { getDefaultAvatarPresetUrl } from '@/common/avatar';
import DefaultAvatar from '@/assets/images/default-avatar.svg';

interface IProps {
  /** avatar url */
  avatar: string | { type: string; gravatar: string; custom: string };
  /** size 48 96 128 256 */
  size: string;
  searchStr?: string;
  className?: string;
  alt: string;
}

const Index: FC<IProps> = ({
  avatar,
  size,
  className,
  searchStr = '',
  alt,
}) => {
  let url = '';

  const appendAvatarParams = (
    source: string,
    params = '',
    useIdenticon = false,
  ) => {
    const normalized = source.trim();
    if (!normalized) {
      return '';
    }
    if (normalized.startsWith('data:') || normalized.startsWith('blob:')) {
      return normalized;
    }

    const query = [
      params,
      useIdenticon && !/(^|&)d=/.test(params) ? 'd=identicon' : '',
    ]
      .filter(Boolean)
      .join('&');
    if (!query) {
      return normalized;
    }
    return `${normalized}${normalized.includes('?') ? '&' : '?'}${query}`;
  };

  if (typeof avatar === 'string') {
    if (avatar.length > 1) {
      url = appendAvatarParams(avatar, searchStr, avatar.includes('gravatar'));
    }
  } else if (avatar?.type === 'gravatar' && avatar.gravatar) {
    url = appendAvatarParams(avatar.gravatar, searchStr, true);
  } else if (avatar?.type === 'custom' && avatar.custom) {
    url = appendAvatarParams(avatar.custom, searchStr);
  } else if (avatar?.type === 'default' && avatar.custom) {
    url = getDefaultAvatarPresetUrl(avatar.custom);
  }

  const roundedCls =
    className && className.indexOf('rounded') !== -1 ? '' : 'rounded-circle';

  return (
    <img
      src={url || DefaultAvatar}
      width={size}
      height={size}
      className={classNames(roundedCls, className)}
      alt={alt}
      style={{ objectFit: 'cover', flexShrink: 0 }}
      onError={(evt) => {
        const img = evt.currentTarget;
        if (img.dataset.fallbackApplied) {
          return;
        }
        img.dataset.fallbackApplied = 'true';
        img.src = DefaultAvatar;
      }}
    />
  );
};

export default memo(Index);
